import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx';
import {
  Box,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  InputAdornment,
  IconButton,
  Drawer,
  Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';
const formatDate = (dateString) => {
  if (!dateString) return '';
  const cleanDate = toYYYYMMDD(dateString);
  if (!cleanDate.includes('-')) return cleanDate;
  const [y, m, d] = cleanDate.split('-');
  return `${d}/${m}/${y}`;
};
const toYYYYMMDD = (dateString) => {
  if (!dateString) return '';
  if (dateString.includes('T') || dateString.includes(' ')) {
    return dateString.split(/[T ]/)[0];
  }
  return dateString;
};

export default function NewForm({ userid }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    id: null,
    userid: null,
    name: '',
    gender: 'Male',
    mobile: '',
    aadhar: '',
    epic_no: '',
    vidhansabha: 'Karad South',
    karad: { village: '', ganName: '', zpName: '', reference: '' },
    other: { village: '', taluka: '', district: '' },
    booth_no: '',
    formDate: '',
    ackNo: '',
    wfcDate: '',
    wfcLocation: 'Koyana Vasahat',
    wfcOtherLocation: '',
    wfcRemarks: '',
    operator: '',
  });
  const [editing, setEditing] = useState(false);
  const [filters, setFilters] = useState({
    name: '',
    mobile: '',
    aadhar: '',
    village: '',
    formDateFrom: '',
    formDateTo: '',
    wfcDateFrom: '',
    wfcDateTo: '',
    ackNo: '',
    operator: ''
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/new-form-records`);
      if (response.ok) {
        const data = await response.json();
        setRecords(data);
      } else {
        console.error('Failed to fetch records');
      }
    } catch (error) {
      console.error('Error fetching records:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    if (name.startsWith('karad.')) {
      const k = name.split('.')[1];
      setForm((s) => ({ ...s, karad: { ...s.karad, [k]: value } }));
      return;
    }
    if (name.startsWith('other.')) {
      const k = name.split('.')[1];
      setForm((s) => ({ ...s, other: { ...s.other, [k]: value } }));
      return;
    }
    setForm((s) => ({ ...s, [name]: value }));
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();

    // Prepare payload by mapping frontend state to backend fields
    const payload = {
      userid: userid, // Ensure userid is passed from props
      name: form.name,
      gender: form.gender,
      mobile: form.mobile,
      aadhar: form.aadhar,
      epic_no: form.epic_no,
      vidhansabha: form.vidhansabha,
      // Helper logic for conditional fields can be done inline or simplified
      village: form.vidhansabha === 'Karad South' ? form.karad.village : form.other.village,
      booth_no: form.booth_no,
      gan_name: form.vidhansabha === 'Karad South' ? form.karad.ganName : null,
      zp_name: form.vidhansabha === 'Karad South' ? form.karad.zpName : null,
      reference: form.vidhansabha === 'Karad South' ? form.karad.reference : null,
      taluka: form.other.taluka || null,
      district: form.other.district || null,
      form_date: form.formDate || null,
      ack_no: form.ackNo,
      wfc_date: form.wfcDate || null,
      wfc_location: form.wfcLocation,
      wfc_other_location: form.wfcOtherLocation,
      wfc_remarks: form.wfcRemarks,
      operator: form.operator,
    };

    try {
      let response;
      if (form.id) {
        // Update existing record
        response = await fetch(`${API_BASE}/new-form-records/${form.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
      } else {
        // Create new record
        response = await fetch(`${API_BASE}/new-form-records`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
      }

      if (response.ok) {
        fetchRecords(); // Refresh list to get updated data (including IDs)
        resetForm();
        setDrawerOpen(false);
      } else {
        const errorData = await response.json();
        alert('Failed to save record: ' + (errorData.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving record:', error);
      alert('Error saving record. Please try again.');
    }
  }

  const handleEdit = useCallback((id) => {
    const rec = records.find((r) => r.id === id);
    if (rec) {
      // Map backend fields back to frontend state
      setForm({
        id: rec.id,
        userid: rec.userid,
        name: rec.name,
        gender: rec.gender || 'Male',
        mobile: rec.mobile || '',
        aadhar: rec.aadhar || '',
        epic_no: rec.epic_no || '',
        vidhansabha: rec.vidhansabha || 'Karad South',
        karad: {
          village: rec.vidhansabha === 'Karad South' ? rec.village || '' : '',
          ganName: rec.gan_name || '',
          zpName: rec.zp_name || '',
          reference: rec.reference || ''
        },
        other: {
          village: rec.vidhansabha !== 'Karad South' ? rec.village || '' : '',
          taluka: rec.taluka || '',
          district: rec.district || ''
        },
        booth_no: rec.booth_no || '',
        formDate: toYYYYMMDD(rec.form_date) || '',
        ackNo: rec.ack_no || '',
        wfcDate: toYYYYMMDD(rec.wfc_date) || '',
        wfcLocation: rec.wfc_location || 'Koyana Vasahat',
        wfcOtherLocation: rec.wfc_other_location || '',
        wfcRemarks: rec.wfc_remarks || '',
        operator: rec.operator || '',
      });
      setEditing(true);
      setDrawerOpen(true);
    }
  }, [records]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      const response = await fetch(`${API_BASE}/new-form-records/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        // Optimistic update or refetch
        setRecords((r) => r.filter((x) => x.id !== id));
      } else {
        alert('Failed to delete record');
      }
    } catch (error) {
      console.error('Error deleting record:', error);
      alert('Error deleting record');
    }
  }, []);

  const handleFilterChange = useCallback((e) => {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value }));
  }, []);

  const filteredRecords = useMemo(() => {
    const m = (val = '') => (val == null ? '' : String(val).toLowerCase());
    return records.filter((r) => {
      const formDateMatch = (!filters.formDateFrom || r.form_date >= filters.formDateFrom) &&
                            (!filters.formDateTo || r.form_date <= filters.formDateTo);
      const wfcDateMatch = (!filters.wfcDateFrom || r.wfc_date >= filters.wfcDateFrom) &&
                           (!filters.wfcDateTo || r.wfc_date <= filters.wfcDateTo);

      return (
        m(r.name).includes(m(filters.name)) &&
        m(r.mobile).includes(m(filters.mobile)) &&
        m(r.aadhar).includes(m(filters.aadhar)) &&
        m(r.village).includes(m(filters.village)) &&
        formDateMatch &&
        wfcDateMatch &&
        m(r.ack_no).includes(m(filters.ackNo)) &&
        m(r.operator).includes(m(filters.operator))
      );
    });
  }, [records, filters]);

  const handleDownloadExcel = () => {
    const dataToExport = filteredRecords.map(r => ({
      Name: r.name,
      Gender: r.gender,
      Mobile: r.mobile,
      Aadhar: r.aadhar,
      'MH No': r.epic_no,
      Vidhansabha: r.vidhansabha,
      Village: r.village,
      'Booth No': r.booth_no,
      'Gan Name': r.gan_name,
      'ZP Name': r.zp_name,
      Reference: r.reference,
      'Form Date': formatDate(r.form_date),
      'Ack No': r.ack_no,
      Taluka: r.taluka,
      District: r.district,
      'WFC Date': formatDate(r.wfc_date),
      'WFC Location': r.wfc_location,
      'WFC Other Location': r.wfc_other_location,
      'WFC Remarks': r.wfc_remarks,
      Operator: r.operator,
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'NewForms');
    XLSX.writeFile(workbook, `NewForms_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  function resetForm() {
    setForm({
      id: null,
      userid: null,
      name: '',
      gender: 'Male',
      mobile: '',
      aadhar: '',
      epic_no: '',
      vidhansabha: 'Karad South',
      karad: { village: '', ganName: '', zpName: '', reference: '' },
      other: { village: '', taluka: '', district: '' },
      booth_no: '',
      formDate: '',
      ackNo: '',
      wfcDate: '',
      wfcLocation: 'Koyana Vasahat',
      wfcOtherLocation: '',
      wfcRemarks: '',
      operator: '',
    });
    setEditing(false);
    setDrawerOpen(false);
  }

  return (
    <Box sx={{ p: 1 }}>
      <Paper sx={{ p: 2, bgcolor: 'white' }} elevation={4}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
          <Typography variant="h6" sx={{ color: '#0b4a6f', fontWeight: 'bold' }}>New Form Records</Typography>
          <Chip label={`${records.length} records`} color="primary" variant="outlined" size="small" />
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            color="success"
            onClick={handleDownloadExcel}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold', mr: 1 }}
          >
            Download Excel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => { resetForm(); setDrawerOpen(true); }}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 'bold' }}
          >
            Add New Form
          </Button>
        </Box>

        {useMemo(() => (
          <TableContainer sx={{ maxHeight: 'calc(100vh - 200px)', border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {['Name', 'Mobile', 'Aadhar', 'Village', 'Taluka', 'District', 'Form Date', 'WFC Date', 'Ack No', 'Operator', 'Actions'].map((h) => (
                    <TableCell key={h} sx={{ bgcolor: '#0b4a6f', color: 'white', fontWeight: 'bold', py: 1.5 }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="name" value={filters.name} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="primary" /></InputAdornment>, disableUnderline: true }} placeholder="Search..." /></TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="mobile" value={filters.mobile} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="aadhar" value={filters.aadhar} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="village" value={filters.village} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }} />
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }} />
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}>
                    <Stack spacing={0.5}>
                      <TextField size="small" variant="standard" type="date" name="formDateFrom" value={filters.formDateFrom} onChange={handleFilterChange} InputProps={{ disableUnderline: true }} sx={{ bgcolor: 'white', px: 0.5, border: '1px solid #999', fontSize: '10px' }} />
                      <TextField size="small" variant="standard" type="date" name="formDateTo" value={filters.formDateTo} onChange={handleFilterChange} InputProps={{ disableUnderline: true }} sx={{ bgcolor: 'white', px: 0.5, border: '1px solid #999', fontSize: '10px' }} />
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}>
                    <Stack spacing={0.5}>
                      <TextField size="small" variant="standard" type="date" name="wfcDateFrom" value={filters.wfcDateFrom} onChange={handleFilterChange} InputProps={{ disableUnderline: true }} sx={{ bgcolor: 'white', px: 0.5, border: '1px solid #999', fontSize: '10px' }} />
                      <TextField size="small" variant="standard" type="date" name="wfcDateTo" value={filters.wfcDateTo} onChange={handleFilterChange} InputProps={{ disableUnderline: true }} sx={{ bgcolor: 'white', px: 0.5, border: '1px solid #999', fontSize: '10px' }} />
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="ackNo" value={filters.ackNo} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }}><TextField size="small" variant="standard" name="operator" value={filters.operator} onChange={handleFilterChange} sx={{ bgcolor: 'white', borderRadius: 1, px: 0.5, border: '1px solid black' }} InputProps={{ disableUnderline: true }} placeholder="Search..." /></TableCell>
                  <TableCell sx={{ bgcolor: '#e1f5fe', py: 1 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRecords.map((r) => (
                  <TableRow key={r.id} hover sx={{ '&:nth-of-type(even)': { bgcolor: '#fafafa' } }}>
                    <TableCell sx={{ fontWeight: 500 }}>{r.name}</TableCell>
                    <TableCell>{r.mobile}</TableCell>
                    <TableCell>{r.aadhar}</TableCell>
                    <TableCell>{r.village}</TableCell>
                    <TableCell>{r.taluka}</TableCell>
                    <TableCell>{r.district}</TableCell>
                    <TableCell>{formatDate(r.form_date)}</TableCell>
                    <TableCell>{formatDate(r.wfc_date)}</TableCell>
                    <TableCell>{r.ack_no}</TableCell>
                    <TableCell>{r.operator}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <IconButton size="small" color="primary" onClick={() => handleEdit(r.id)} sx={{ bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' } }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDelete(r.id)} sx={{ bgcolor: '#ffebee', '&:hover': { bgcolor: '#ffcdd2' } }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {loading && (
                  <TableRow>
                    <TableCell colSpan={11} align="center">Loading...</TableCell>
                  </TableRow>
                )}
                {!loading && filteredRecords.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11} align="center">No records found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        ), [filteredRecords, filters, handleFilterChange, handleEdit, handleDelete, loading])}
      </Paper>

      <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box component="form" onSubmit={handleSubmit} sx={{ width: { xs: '100vw', sm: 500 }, p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: '#0b4a6f', flexGrow: 1, fontWeight: 'bold' }}>
              {editing ? 'Edit Record' : 'Add New Record'}
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', flexGrow: 1, pr: 1 }}>
            <TextField label="Name" name="name" value={form.name} onChange={handleChange} required fullWidth variant="outlined" />

            <FormControl fullWidth>
              <InputLabel id="gender-label">Gender</InputLabel>
              <Select labelId="gender-label" label="Gender" name="gender" value={form.gender} onChange={handleChange}>
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
              </Select>
            </FormControl>

            <TextField label="Mobile No" name="mobile" value={form.mobile} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="Aadhar No" name="aadhar" value={form.aadhar} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="MH No" name="epic_no" value={form.epic_no} onChange={handleChange} fullWidth variant="outlined" />

            <FormControl fullWidth>
              <InputLabel id="vidh-label">Vidhansabha</InputLabel>
              <Select labelId="vidh-label" label="Vidhansabha" name="vidhansabha" value={form.vidhansabha} onChange={handleChange}>
                <MenuItem value="Karad South">Karad South</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>

            {form.vidhansabha === 'Karad South' ? (
              <Box sx={{ borderLeft: '3px solid #00acc1', pl: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField label="Village" name="karad.village" value={form.karad.village} onChange={handleChange} fullWidth variant="outlined" />
                <TextField label="Booth No" name="booth_no" value={form.booth_no} onChange={handleChange} fullWidth variant="outlined" />
                <TextField label="Gan Name" name="karad.ganName" value={form.karad.ganName} onChange={handleChange} fullWidth variant="outlined" />
                <TextField label="ZP Name" name="karad.zpName" value={form.karad.zpName} onChange={handleChange} fullWidth variant="outlined" />
                <TextField label="Reference" name="karad.reference" value={form.karad.reference} onChange={handleChange} fullWidth variant="outlined" />
              </Box>
            ) : (
              <Box sx={{ borderLeft: '3px solid #8e24aa', pl: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField label="Village" name="other.village" value={form.other.village} onChange={handleChange} fullWidth variant="outlined" />
                <TextField label="Booth No" name="booth_no" value={form.booth_no} onChange={handleChange} fullWidth variant="outlined" />
              </Box>
            )}

            <TextField label="Taluka" name="other.taluka" value={form.other.taluka} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="District" name="other.district" value={form.other.district} onChange={handleChange} fullWidth variant="outlined" />

            <TextField label="Form Date" name="formDate" type="date" InputLabelProps={{ shrink: true }} value={form.formDate} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="Acknowledgement No." name="ackNo" value={form.ackNo} onChange={handleChange} fullWidth variant="outlined" />
            <TextField label="WFC Visit Date" name="wfcDate" type="date" InputLabelProps={{ shrink: true }} value={form.wfcDate} onChange={handleChange} fullWidth variant="outlined" />

            <FormControl fullWidth>
              <InputLabel id="wfc-loc-label">WFC Visit Location</InputLabel>
              <Select labelId="wfc-loc-label" label="WFC Visit Location" name="wfcLocation" value={form.wfcLocation} onChange={handleChange}>
                <MenuItem value="Koyana Vasahat">Koyana Vasahat</MenuItem>
                <MenuItem value="Banawadi">Banawadi</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>
            </FormControl>

            {form.wfcLocation === 'Other' && (
              <TextField label="Other WFC Location" name="wfcOtherLocation" value={form.wfcOtherLocation} onChange={handleChange} fullWidth variant="outlined" />
            )}

            <TextField label="WFC Visit Remarks" name="wfcRemarks" value={form.wfcRemarks} onChange={handleChange} multiline rows={3} fullWidth variant="outlined" />

            <TextField label="Operator Name" name="operator" value={form.operator} onChange={handleChange} fullWidth variant="outlined" />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={2}>
            <Button type="submit" variant="contained" color="primary" fullWidth size="large" sx={{ borderRadius: 2 }}>
              {editing ? 'Update' : 'Save'}
            </Button>
            <Button variant="outlined" onClick={resetForm} fullWidth size="large" sx={{ borderRadius: 2 }}>
              Reset
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </Box>
  );
}
